import { useState, useMemo, useRef } from 'react';
import { NavLink as RouterLink, useParams, useLocation } from 'react-router';

import { FileCopy, ImageSearch } from '@mui/icons-material';
import Add from '@mui/icons-material/Add';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import {
  Box,
  CardActionArea,
  Grid,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import {
  getGridNumericOperators,
  GridActionsCellItem,
  GridFilterModel,
  GridRenderCellParams,
  GridToolbar,
  GridValueFormatterParams
} from '@mui/x-data-grid';

import { getEvaluation } from '@/api/evaluations';
import Page from '@/components/Page';
import Button from '@/components/button';
import Card from '@/components/card';
import DataTable from '@/components/dataTable';
import NoRowsOverlay from '@/components/dataTable/customNoRowsOverlay/NoRowsOverlay';
import OnBoardingOverlay from '@/components/dataTable/customNoRowsOverlay/OnBoardingOverlay';
import authorsOperator from '@/components/dataTable/customOperators/authorsOperator';
import dateRangeOperator from '@/components/dataTable/customOperators/dateRangeOperator';
import Dialog from '@/components/dialog';
import Header from '@/components/header';
import Toolbar from '@/components/toolbar';
import { EvaluationSteps } from '@/constants/driverSteps';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/constants/table';
import {
  useDeleteEvaluation,
  useFetchEvaluations
} from '@/hooks/queries/evaluations';
import { useAppSelector } from '@/hooks/redux';
import useAlert from '@/hooks/useAlert';
import useDriver from '@/hooks/useDriver';
import useTableParams from '@/hooks/useTableParams';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { Evaluation } from '@/models/evaluations';
import LocalStorageService from '@/services/LocalStorageService';
import {
  formatDate,
  getDateWithTime,
  getPrettyDate
} from '@/utils/datesAndTime';
import getRoute from '@/utils/getRoute';
import nFormatter from '@/utils/nFormatter';
import Error from '@/views/misc/error';

const EvaluationsOnBoardingOverlay = () => (
  <OnBoardingOverlay
    icon={<ImageSearch color="action" sx={{ width: 40, height: 40 }} />}
    title="Start list of Evaluations"
    text="Create an evaluation to start analising social media posts"
  />
);

const defaultParams = {
  page: DEFAULT_PAGE,
  size: DEFAULT_PAGE_SIZE,
  sort: undefined,
  order: undefined,
  name: undefined,
  author: undefined,
  creationDate: undefined,
  lastModify: undefined,
  totalItems: undefined
};

const FILTER_FIELD_MAPPING = {
  authorName: 'author',
  createdAt: 'creationDate',
  updatedAt: 'lastModify',
  totalItems: 'totalItems'
} as const;

const FILTERABLE_FIELDS = Object.keys(FILTER_FIELD_MAPPING);
const FILTER_PARAM_NAMES = Object.values(FILTER_FIELD_MAPPING);

const getInitialFilterModelItems = (params: Record<string, any>) => {
  const { author, creationDate, lastModify, totalItems } = params;
  const items = [];

  if (author) {
    items.push({
      field: 'authorName',
      id: items.length,
      operator: 'isAnyOf',
      value: author.split(',')
    });
  }

  if (creationDate) {
    items.push({
      field: 'createdAt',
      id: items.length,
      operator: 'between',
      value: creationDate.split(',').map((item: string) => new Date(item))
    });
  }

  if (lastModify) {
    items.push({
      field: 'updatedAt',
      id: items.length,
      operator: 'between',
      value: lastModify.split(',').map((item: string) => new Date(item))
    });
  }

  if (totalItems !== undefined) {
    items.push({
      field: 'totalItems',
      id: items.length,
      operator: '=',
      value: Number(totalItems)
    });
  }

  return items;
};

const GridToolbarCustom = () => (
  <Box
    sx={{
      '& .MuiButton-root': {
        color: 'text.primary'
      },
      '& .MuiSvgIcon-root': {
        color: ' text.primary'
      }
    }}
  >
    <GridToolbar />
  </Box>
);

const EvaluationsList = () => {
  const theme = useTheme();
  const alert = useAlert();
  const navigate = useWorkspaceNavigate();
  const location = useLocation();
  const isDemoActive = location.hash === '#demo';
  const workspaceId = Number(useParams<{ workspaceId: string }>().workspaceId);
  const { params, handlers, updateParams } = useTableParams(
    getRoute.evaluations.LIST(),
    defaultParams
  );

  const user = useAppSelector((state) => state.user.currentUser);
  const goTo = (path: string) =>
    navigate(isDemoActive ? { pathname: path, hash: '#demo' } : path);

  const [isDeleteDialogOpen, setDeleteIsDialogOpen] = useState<boolean>(false);
  const [selectedEvaluations, setSelectedEvaluations] = useState<number[]>([]);
  const [selectedView, setSelectedView] = useState<string>(
    LocalStorageService.getItem('evaluations-list-view') || 'table'
  );

  const initialFilterModel = useRef({
    items: getInitialFilterModelItems(params)
  });

  const queryParams = useMemo(
    () => ({
      page: params.page,
      size: params.size,
      name: params.name && params.name.length >= 2 ? params.name : undefined,
      sort: params.sort,
      order: params.order,
      ...Object.fromEntries(
        FILTER_PARAM_NAMES.map((param) => [param, params[param]])
      )
    }),
    [params]
  );

  const {
    data: evaluationsResponse,
    isLoading,
    refetch,
    error
  } = useFetchEvaluations(queryParams);

  const evaluations = evaluationsResponse?.results || [];
  const totalItems = evaluationsResponse?.totalItems || 0;

  const handleFilterModelChange = (model: GridFilterModel) => {
    const newParams: Record<string, any> = {};

    model.items.forEach((item) => {
      const { field, value } = item;

      if (value == null || value === '') return;

      if (field === 'authorName') {
        newParams.author = Array.isArray(value)
          ? value.join(',')
          : String(value);
      } else if (field === 'createdAt') {
        const formattedDates: string[] = value
          .map((date: Date) => formatDate(date, 'yyyy-MM-dd'))
          .filter(Boolean);

        newParams.creationDate = formattedDates.length
          ? formattedDates.join(',')
          : undefined;
      } else if (field === 'updatedAt') {
        const formattedDates: string[] = value
          .map((date: Date) => formatDate(date, 'yyyy-MM-dd'))
          .filter(Boolean);
        newParams.lastModify = formattedDates.length
          ? formattedDates.join(',')
          : undefined;
      } else if (field === 'totalItems') {
        const numberValue = Number(value);

        if (Number.isInteger(numberValue) && numberValue > 0) {
          newParams.totalItems = String(numberValue);
        }
      }
    });

    const activeFields = new Set(model.items.map((item) => item.field));

    FILTERABLE_FIELDS.forEach((field) => {
      if (!activeFields.has(field)) {
        const paramName =
          FILTER_FIELD_MAPPING[field as keyof typeof FILTER_FIELD_MAPPING];
        newParams[paramName] = undefined;
      }
    });

    updateParams({ ...newParams });
  };

  // Memoized authors list for the filter
  const authors = useMemo(
    () =>
      Array.from(
        new Set(evaluations.map((evaluation) => evaluation.authorName))
      ).map((author) => ({ value: author, label: author })),
    [evaluations]
  );

  const { mutate: deleteEvaluation, isPending: isDeleting } =
    useDeleteEvaluation(
      () => {
        const firstSelectedEvaluationName = evaluations.find(
          (evaluation) => evaluation.id === selectedEvaluations[0]
        )?.title;

        const alertMessage =
          selectedEvaluations.length === 1
            ? `"${firstSelectedEvaluationName}" has been successfully deleted`
            : `${selectedEvaluations.length} evaluations have been successfully deleted`;

        alert.info(alertMessage);
        handleCloseDeleteDialog(true);
        refetch();
      },
      () => alert.error(`Some error occurred while deleting the evaluations`)
    );

  const handleCloseDeleteDialog = (clearSelection = false) => {
    setDeleteIsDialogOpen(false);
    if (clearSelection) setSelectedEvaluations([]);
  };

  const handleDeleteEvaluations = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    deleteEvaluation({ ids: selectedEvaluations });
  };

  const handleCopyEvaluation = (id: number) => {
    getEvaluation(id)
      .then(({ data }) => {
        navigate(getRoute.evaluations.CREATE(), undefined, {
          state: { evaluationCopy: data }
        });
      })
      .catch(() =>
        alert.error('Something went wrong while copying the evaluation')
      );
  };

  const handleChangeView = (
    _: React.MouseEvent<HTMLElement>,
    newView: string
  ) => {
    if (newView !== null) {
      // Prevent deselection
      setSelectedView(newView);
      setSelectedEvaluations([]);
      LocalStorageService.setItem('evaluations-list-view', newView);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Evaluation Title',
        width: 100,
        flex: 1,
        filterable: false,
        renderCell: (data: GridRenderCellParams<Evaluation.ListItem>) => {
          const { title } = data.row;

          return (
            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {title}
            </Typography>
          );
        }
      },
      {
        field: 'createdAt',
        headerName: 'Creation Date',
        width: 100,
        flex: 1,
        filterable: true,
        filterOperators: dateRangeOperator,
        valueFormatter: (
          params: GridValueFormatterParams<Evaluation.ListItem>
        ) => getDateWithTime(params.value.toString()),
        valueGetter: (params: GridRenderCellParams<Evaluation.ListItem>) => {
          const { createdAt } = params.row;
          return new Date(createdAt);
        },
        renderCell: (data: GridRenderCellParams<Evaluation.ListItem>) => {
          const { createdAt } = data.row;
          return <Typography>{getPrettyDate(createdAt)}</Typography>;
        }
      },
      {
        field: 'updatedAt',
        headerName: 'Last Modified',
        width: 100,
        flex: 1,
        filterable: true,
        filterOperators: dateRangeOperator,
        valueFormatter: (
          params: GridValueFormatterParams<Evaluation.ListItem>
        ) => getDateWithTime(params.value.toString()),
        valueGetter: (params: GridRenderCellParams<Evaluation.ListItem>) => {
          const { createdAt } = params.row;
          return new Date(createdAt);
        },
        renderCell: (data: GridRenderCellParams<Evaluation.ListItem>) => {
          const { updatedAt, createdAt } = data.row;
          return (
            <Typography>{getPrettyDate(updatedAt || createdAt)}</Typography>
          );
        }
      },
      {
        field: 'authorName',
        headerName: 'Author',
        width: 100,
        flex: 1,
        filterable: true,
        type: 'singleSelect',
        filterOperators: authorsOperator,
        renderCell: (data: GridRenderCellParams<Evaluation.ListItem>) => {
          const { authorName } = data.row;
          return <Typography>{authorName}</Typography>;
        }
      },
      {
        field: 'totalItems',
        headerName: 'Total Items',
        minWidth: 150,
        type: 'number',
        filterable: true,
        filterOperators: getGridNumericOperators().filter(
          (operator) => operator.value === '='
        ),
        valueFormatter: (
          params: GridValueFormatterParams<Evaluation.ListItem>
        ) => params.value,
        renderCell: (data: GridRenderCellParams<Evaluation.ListItem>) => {
          const { totalItems } = data.row;
          return <Typography>{nFormatter(totalItems)}</Typography>;
        }
      },
      {
        field: 'actions',
        type: 'actions',
        width: 20,
        getActions: (data: GridRenderCellParams<Evaluation.ListItem>) => [
          <GridActionsCellItem
            icon={<FileCopy />}
            key={`copy-${data.row.id}`}
            label="Copy Evaluation"
            showInMenu
            onClick={() => handleCopyEvaluation(data.row.id)}
          />
        ]
      }
    ],
    [authors]
  );

  const isDeleteButtonDisabled = selectedEvaluations?.length <= 0;

  const gridItems = Array.from({ length: 18 }, (_, index) => index + 1);

  let emptyStateEvaluations = EvaluationsOnBoardingOverlay;
  if (
    evaluations?.length === 0 &&
    Object.entries(queryParams)
      .filter(([key]) => key !== 'page' && key !== 'size')
      .some(([, v]) => v !== undefined)
  ) {
    emptyStateEvaluations = () => <NoRowsOverlay />;
  }

  if (params.name !== undefined) {
    emptyStateEvaluations = () => <NoRowsOverlay />;
  }

  useDriver({
    isLoading,
    steps: EvaluationSteps
  });

  if (error)
    return (
      <Page>
        <Error message="Failed to get evaluations" error={error} />
      </Page>
    );

  return (
    <>
      <Page
        title="Evaluations"
        header={
          <Header
            actions={
              <>
                <ToggleButtonGroup
                  value={selectedView}
                  exclusive
                  onChange={handleChangeView}
                  aria-label="view"
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.background.paper
                  }}
                >
                  <ToggleButton
                    value="card"
                    aria-label="card view"
                    disabled={selectedView === 'card'}
                  >
                    <CalendarViewMonthIcon />
                  </ToggleButton>
                  <ToggleButton
                    value="table"
                    aria-label="table view"
                    disabled={selectedView === 'table'}
                  >
                    <ViewListOutlinedIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  id="evaluation-create"
                  onClick={() => goTo(getRoute.evaluations.CREATE())}
                  text="create evaluation"
                  startIcon={<Add />}
                />
              </>
            }
            title="Evaluations"
          />
        }
        toolbar={
          <Toolbar
            hasSelection={selectedEvaluations}
            onClickDelete={() => setDeleteIsDialogOpen(true)}
            onChangeSearch={handlers.onChangeSearch}
            showDeleteButton={!isDeleteButtonDisabled}
            showSearchInput
            searchValue={params.name ? String(params.name) : ''}
            totalItems={totalItems}
          />
        }
      >
        <>
          {selectedView === 'card' && isLoading && (
            <Grid container spacing={2}>
              {gridItems.map((number) => (
                <Grid item xs={12} sm={6} md={4} key={number}>
                  <Skeleton variant="rectangular" height={176} />
                </Grid>
              ))}
            </Grid>
          )}

          {selectedView === 'card' &&
            !isLoading &&
            evaluations.length !== 0 && (
              <>
                <Grid container spacing={2}>
                  {evaluations.map((evaluation) => (
                    <Grid item xs={12} sm={6} md={4} key={evaluation.id}>
                      <CardActionArea
                        component={RouterLink}
                        to={`/workspace/${workspaceId}${getRoute.evaluations.DETAIL(
                          evaluation.id
                        )}`}
                      >
                        <Card
                          title={
                            <Typography variant="h6">
                              {evaluation.title}
                            </Typography>
                          }
                          elevation={3}
                          titleEllipsis
                          content={
                            <Stack gap={0.5}>
                              <Typography variant="body2" color="textSecondary">
                                {evaluation.totalItems} items -{' '}
                                {evaluation.updatedAt
                                  ? getPrettyDate(evaluation.updatedAt)
                                  : getPrettyDate(evaluation.createdAt)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Created by {evaluation.authorName}
                              </Typography>
                            </Stack>
                          }
                          actions={
                            <>
                              {evaluation.authorId === user.id && (
                                <Button
                                  color="error"
                                  variant="text"
                                  text="delete"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setDeleteIsDialogOpen(true);
                                    setSelectedEvaluations([evaluation.id]);
                                  }}
                                />
                              )}
                              <Button
                                variant="text"
                                text="copy"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCopyEvaluation(evaluation.id);
                                }}
                              />
                            </>
                          }
                        />
                      </CardActionArea>
                    </Grid>
                  ))}
                </Grid>
                <TablePagination
                  component="div"
                  count={totalItems}
                  page={Number(params.page) - 1}
                  rowsPerPage={Number(params.size)}
                  onPageChange={(_, page) =>
                    handlers.onChangePage(String(page + 1))
                  }
                  onRowsPerPageChange={(e) =>
                    handlers.onChangePageSize(String(e.target.value))
                  }
                />
              </>
            )}

          {selectedView === 'card' &&
            !isLoading &&
            evaluations.length === 0 && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <CardActionArea>
                      <Stack
                        sx={{
                          boxShadow:
                            '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
                          background: theme.palette.primary.main,
                          height: '176px'
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          gap={1}
                        >
                          <Add
                            sx={{ color: theme.palette.primary.contrastText }}
                          />
                          <Typography
                            sx={{ color: theme.palette.primary.contrastText }}
                            color="primary"
                            variant="subtitle2"
                          >
                            CREATE EVALUATION
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardActionArea>
                  </Grid>
                  {gridItems.slice(0, 8).map((id) => (
                    <Grid item xs={12} sm={6} md={4} key={id}>
                      <Box
                        sx={{
                          border: '1px dashed rgba(0, 0, 0, 0.18);',
                          boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.15)',
                          filter:
                            'drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.13)) drop-shadow(0px 1px 9px rgba(0, 0, 0, 0.12))'
                        }}
                        width={373}
                        height={176}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

          {selectedView === 'table' && (
            <DataTable
              checkboxSelection
              columns={columns}
              onRowSelectionModelChange={(newSelection) =>
                setSelectedEvaluations(newSelection as number[])
              }
              rowHeight={72}
              rows={evaluations}
              rowSelectionModel={selectedEvaluations}
              loading={isLoading}
              onRowClick={(row) =>
                navigate(getRoute.evaluations.DETAIL(row.id))
              }
              isRowSelectable={(data) => data.row.authorId === user.id}
              rowCount={totalItems}
              paginationMode="server"
              filterMode="server"
              initialState={{
                filter: {
                  filterModel: initialFilterModel.current
                }
              }}
              onChangePageSize={handlers.onChangePageSize}
              onChangePage={handlers.onChangePage}
              onSortModelChange={handlers.onSortModelChange}
              onFilterModelChange={(model) => handleFilterModelChange(model)}
              sort={params.sort}
              order={params.order}
              page={params.page}
              size={params.size}
              toolbar={GridToolbarCustom}
              noRowsOverlay={emptyStateEvaluations}
            />
          )}
        </>
      </Page>
      <Dialog
        title="Delete Evaluation"
        dialogContent={
          selectedEvaluations.length === 1 ? (
            <>
              <Typography>
                Are you sure you want to delete &ldquo;
                {
                  evaluations.find((item) => item.id === selectedEvaluations[0])
                    ?.title
                }
                &ldquo;?
              </Typography>
              <Typography>
                You will not be able to recover this evaluation.
              </Typography>
            </>
          ) : (
            <>
              <Typography>
                Are you sure you want to delete {selectedEvaluations.length}{' '}
                evaluations?
              </Typography>
              <Typography>You will not be able to recover these.</Typography>
            </>
          )
        }
        open={isDeleteDialogOpen}
        onClose={() => handleCloseDeleteDialog(false)}
        actions={
          <>
            <Button
              variant="text"
              text="cancel"
              onClick={() => handleCloseDeleteDialog(false)}
              disabled={isDeleting}
            />
            <Button
              color="error"
              text="delete"
              onClick={handleDeleteEvaluations}
              loading={isDeleting}
            />
          </>
        }
      />
    </>
  );
};

export default EvaluationsList;
